import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ApiService } from "app/services/api.service";
import { Location, NgClass, DatePipe } from "@angular/common";
import { LocationModel } from "app/models/location.model";
import { ActivatedRoute } from "@angular/router";
import { StaffModel } from "app/models/staff.model";
import { Sort, MatSortModule } from "@angular/material/sort";
import { orderBy } from "lodash";
import { Subject, combineLatest, fromEvent, of } from "rxjs";
import { catchError, map, startWith, switchMap, tap } from "rxjs/operators";
import { HighlightSearch } from "../../pipes/pipes";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { LocationInfoComponent } from "../location/location-info/location-info.component";
import { StaffInfoComponent, StaffUpdateEvent } from "./staff-info/staff-info.component";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { CanPipe } from "app/pipes/can.pipe";
import { Areas } from "app/models/permission.model";
import { Angular2Csv } from "angular2-csv";
import moment from "moment-timezone";
import { csvTextCell, sanitizeCsvCell } from "app/shared/csv.shared";
import { IRoleSymbols } from "app/models/permission.model";
import { NO_ROLE_SYMBOLS, anyHasIdentity, staffListIconClasses, withStaffRole } from "app/shared/role-symbol/role-symbol";
import { RoleLegendComponent } from "app/shared/role-symbol/role-legend.component";

@Component({
    selector: "app-staff",
    templateUrl: "./staff.component.html",
    styleUrls: ["./staff.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    NgClass,
    MatCardModule,
    MatSortModule,
    StaffInfoComponent,
    LocationInfoComponent,
    TranslateModule,
    HighlightSearch,
    DatePipe,
    CanPipe,
    RoleLegendComponent
]
})
export class StaffComponent implements OnInit {
  constructor(
    private api: ApiService,
    private route: ActivatedRoute,
    private location: Location,
    private translate: TranslateService
  ) {}

  readonly Areas = Areas;
  customerCollapsed: boolean = true;
  loadingStaffInformation: boolean;
  staffList: StaffModel[];
  locationList: any;
  staffLocationList: any;
  selectedStaff: StaffModel;
  selectedLocation: LocationModel;
  user: any;
  @ViewChild("staffSearchInput", { static: true }) staffSearchInput: ElementRef;
  @ViewChild("staffSearchButton", { static: true })
  staffSearchButton: ElementRef;

  showLocationsWithoutStaff = false;

  queryForFilter = new Subject<string>();
  queryForFilter$ = this.queryForFilter.asObservable();
  filteredStaffList$;

  fullStaffListObs$ = this.api.getStaffList().pipe(catchError(() => of([])));
  filterON;
  filterCriteria: string;
  exportingStaffList = false;
  /** Custom roles and who has them, for the role symbols; built-in symbols only until loaded */
  roleSymbols: IRoleSymbols = NO_ROLE_SYMBOLS;
  readonly featureEnabledFn = (feature: string) => this.featureEnabled(feature);
  /** Requests to reload the role symbols; only the latest request's answer is used */
  private readonly roleSymbolsReload = new Subject<void>();

  parseStaffList(): void {
    //Make deep copy of the locationList into staffLocationList which will include staff data
    this.staffLocationList = JSON.parse(JSON.stringify(this.locationList ?? []));

    if (!this.showCustomerLocation()) {
      this.staffLocationList = this.staffLocationList.filter(
        (location) => !this.isCustomerLocation(location)
      );
    }

    //Loop through all staff members
    for (let i = 0; i < this.staffList.length; i++) {
      //Get location id for current staff
      const locId = this.staffList[i].locationId;
      //Loop through all locations
      for (let j = 0; j < this.staffLocationList.length; j++) {
        //Check if location has same id as current staff
        if (this.staffLocationList[j].id === locId) {
          //Initialize new empty array if it does not exists
          if (!this.staffLocationList[j].staff) {
            this.staffLocationList[j].staff = [];
          }
          //Set all locations closed for default
          this.staffLocationList[j].collapseState = this.filterON;
          //Add staff into location if location was correct
          this.staffLocationList[j].staff.push(this.staffList[i]);
        }
      }
    }

    //Sort the staff lists
    for (let i = 0; i < this.staffLocationList.length; i++) {
      if (this.staffLocationList[i].staff) {
        this.staffLocationList[i].staff.sort((a, b) => {
          return a.fullName.localeCompare(b.fullName);
        });
      }
    }
  }

  // Export is restricted to super users and Everon admins
  canExportStaffList(): boolean {
    return this.user.roleLevel === 101 || this.user.roleLevel === 200;
  }

  exportStaffList(): void {
    this.exportingStaffList = true;
    this.api.getStaffExportList().subscribe({
      next: (res) => {
        const exportList = new Map((res ?? []).map((staff) => [staff.id, staff]));
        const csvHeaders = [
          "ID",
          ...[
            "USERNAME",
            "FIRST_NAME",
            "LAST_NAME",
            "USING_LEVEL",
            "LOCATION",
            "RECEIVER_GROUPS",
            "ALARM_ROUTES",
            "GSM_NUMBER",
            "EMAIL",
            "LAST_LOGIN",
            "LAST_LOGIN_SERVICE",
          ].map((key) => this.translate.instant(key)),
        ];
        const csvData = [];

        // Export staff in the same order and with the same search filter as shown in the list
        this.staffLocationList.forEach((location) => {
          location.staff?.forEach((listStaff: StaffModel) => {
            const staff = exportList.get(listStaff.id) ?? listStaff;
            // User-controlled values are sanitized so they can't be executed as formulas in spreadsheet software
            csvData.push({
              id: csvTextCell(staff.id),
              username: sanitizeCsvCell(staff.username),
              firstName: sanitizeCsvCell(staff.firstName),
              familyName: sanitizeCsvCell(staff.familyName),
              rights: this.getUsingLevelString(staff.usingLevel),
              location: sanitizeCsvCell(location.name),
              receiverGroups: sanitizeCsvCell((staff.teams ?? []).map((team) => team.groupName).join(", ")),
              alarmRoutes: sanitizeCsvCell((staff.alarmRoutes ?? []).map((route) => route.name).join(", ")),
              gsm: csvTextCell(staff.gsmNumber),
              email: sanitizeCsvCell(staff.email),
              lastSeen: staff.lastLoginTime ? moment(staff.lastLoginTime).format("l LTS") : "",
              lastLoginService: staff.lastLoginTime ? staff.lastLoginService ?? "" : "",
            });
          });
        });

        const filename = "staff-" + moment().format("YYYY-MM-DD");
        const options = {
          fieldSeparator: ";",
          quoteStrings: '"',
          decimalseparator: ".",
          showTitle: false,
          headers: csvHeaders,
        };
        //Generate the file and send it to the browser
        new Angular2Csv(csvData, filename, options);
        this.exportingStaffList = false;
      },
      error: () => {
        this.exportingStaffList = false;
      },
    });
  }

  getUsingLevelString(usingLevel: number): string {
    switch (usingLevel) {
      case 119:
        return this.translate.instant("STAFF_EMAAC_OPERATOR");
      case 101:
        return this.translate.instant("STAFF_CUSTOMER_ADMIN");
      case 100:
        return this.translate.instant("STAFF_MAIN_USER");
      case 50:
        return this.translate.instant("STAFF_USER");
      case 40:
        return this.translate.instant("STAFF_ONON_MAIN_USER");
      case 10:
        return this.translate.instant("EveronMobileApp");
      case 0:
        return this.translate.instant("STAFF_READONLY");
      default:
        return String(usingLevel ?? "");
    }
  }

  staffExists(staffLocationList) {
    let staffFound = false;
    staffLocationList?.forEach((location) => {
      if (staffFound) {
        return;
      }
      staffFound = !!location.staff;
    });
    return staffFound;
  }

  /** Role symbol of the staff member (shared with alarm route recipients), lock or shield for the account state */
  getStaffIcon(staff: StaffModel): string {
    return staffListIconClasses(staff, this.roleSymbols);
  }

  /**
   * Load the role symbols at start, and reload them after a staff member is saved, created or given a
   * new role: the assignment wins over the level, so a stale one would keep showing the old role.
   * A failed reload keeps what is shown; without any symbols custom role holders show their level.
   */
  private watchRoleSymbols(): void {
    this.roleSymbolsReload
      .pipe(switchMap(() => this.api.getRoleSymbols().pipe(catchError(() => of(null as IRoleSymbols)))))
      .subscribe((symbols) => {
        if (symbols) this.roleSymbols = symbols;
      });
  }

  /** Someone in the list signs in with a login identity: the legend explains the shield */
  hasIdentityStaff(): boolean {
    return anyHasIdentity(this.staffList);
  }

  loadRoleSymbols(): void {
    this.roleSymbolsReload.next();
  }

  editStaff(staff: StaffModel): void {
    this.selectedLocation = null;
    if (this.selectedStaff === staff) {
      this.selectedStaff = null;
      this.location.go("staff");
      return;
    }
    this.loadingStaffInformation = true;
    this.selectedStaff = null;
    this.location.go("staff/" + staff.id);
    this.api.getStaffInfo(staff.id).subscribe((res) => {
      this.selectedStaff = res;
      this.loadingStaffInformation = false;
    });
  }

  staffUpdated({ staff: staffData, updateUsername, accessRoleId }: StaffUpdateEvent) {
    // A known role change shows at once; the reload then confirms it (and catches what the event can't know)
    if (accessRoleId !== undefined) {
      this.roleSymbols = withStaffRole(this.roleSymbols, staffData.id, accessRoleId);
    }
    this.loadRoleSymbols();

    //Find correct staff from lists
    let staff: StaffModel = this.staffLocationList
      .find((x) => x.id === staffData.locationId)
      .staff.find((y) => y.id === staffData.id);
    //If staf was not found from locationlist (ie. if location was changed) use the data from selectedStaff variable. Properties will be updated into UI later when staff is added under new location
    if (!staff) staff = this.selectedStaff;

    //Update properties for UI
    staff.fullName = staffData.fullName;
    // Keep the row as the staff list API shows it: authenticated staff have "-" as username,
    // while the details API returns their old username, which isn't used for logging in
    staff.identityUsername = staffData.identityUsername;
    if (staffData.identityUsername) {
      staff.username = "-";
    } else if (updateUsername) {
      staff.username = staffData.username;
    }
    staff.usingLevel = staffData.usingLevel;
    staff.locked = staffData.locked;

    //If staff location was changed move it to new location
    if (staffData.locationId !== this.selectedStaff.locationId) {
      //Find the old location
      const oldLocation: any = this.staffLocationList.find((obj) => {
        return obj.id === this.selectedStaff.locationId;
      });
      //Remove staff from old location device list
      oldLocation.staff.splice(
        oldLocation.staff.findIndex(
          (staff) => staff.id === this.selectedStaff.id
        ),
        1
      );
      // oldLocation.staff.splice(oldLocation.staff.indexOf(this.selectedStaff), 1);

      //Find the new location from the staff location list
      for (let i = 0; i < this.staffLocationList.length; i++) {
        if (this.staffLocationList[i].id === staffData.locationId) {
          //Create new empty array if it does not exists
          if (!this.staffLocationList[i].staff) {
            this.staffLocationList[i].staff = [];
          }
          //Add edited staff into new location
          this.staffLocationList[i].staff.push(this.selectedStaff);
          this.selectedStaff.locationId = staffData.locationId;
          break;
        }
      }
    }
    //Sort the staff lists
    for (let i = 0; i < this.staffLocationList.length; i++) {
      if (this.staffLocationList[i].staff) {
        this.staffLocationList[i].staff.sort((a, b) => {
          return a.fullName.localeCompare(b.fullName);
        });
      }
    }
  }

  staffInserted(staff: StaffModel): void {
    // A new staff member can be created with a custom role
    this.loadRoleSymbols();
    this.staffList.push(staff);
    this.staffLocationList = null;
    this.parseStaffList();
    this.selectedStaff = staff;
    this.location.go("staff/" + staff.id);
  }

  staffDeleted(id: string): void {
    this.location.go("staff");
    this.selectedStaff = null;
    //Find correct staff from staff list
    const staff: StaffModel = this.staffList.find((obj) => {
      return obj.id === id;
    });
    //Remove staff from the list
    this.staffList.splice(this.staffList.indexOf(staff), 1);
    this.staffLocationList = null;
    //Parse the staff list with removed staff member
    this.parseStaffList();
  }

  addNewStaff(): void {
    this.selectedStaff = new StaffModel();
  }

  editLocation(location: LocationModel): void {
    this.selectedStaff = null;

    /*
    if (location.id.substring(0, 2) === "C0") {
      return;
    }
    */

    if (location === this.selectedLocation) {
      this.selectedLocation = null;
      return;
    }
    this.selectedLocation = location;
  }

  closeLocation(location: LocationModel) {
    this.selectedLocation = null;
  }

  locationUpdated(location: LocationModel): void {
    //Update name and id for the staff listing
    for (let i = 0; i < this.staffLocationList.length; i++) {
      if (this.staffLocationList[i].id === location.id) {
        this.staffLocationList[i].name = location.name;
      }
    }
    //Update all values into location list
    const locIndex: number = this.locationList.findIndex(
      (x) => x.id === location.id
    );
    this.locationList[locIndex] = location;
  }

  locationAdded(newLocation: LocationModel): void {
    this.locationList.push(newLocation);
  }

  locationDeleted(location: LocationModel): void {
    this.locationList.splice(this.locationList.indexOf(location), 1);
  }

  showCustomerLocation() {
    return this.user.locationId.substring(0, 2) === "C0";
  }

  isCustomerLocation(location: any) {
    return location.id.substring(0, 2) === "C0";
  }

  featureEnabled(feature: string): boolean {
    const features = JSON.parse(localStorage.getItem("features"));
    if (features) {
      const index = features.indexOf(feature);
      if (index !== -1) {
        return true;
      }
    }
    return false;
  }

  private userSortOrder: Record<number, number> = {
    101: 1, //Super user
    100: 2, // Main user
    40: 3,  // OnOn Main user
    50: 4,  // User
    0: 5,   // Read only
    10: 6,  // EMA
    119: 7, // EMAAC Operator
    120: 8  // EMAAC account
  };

  sortData(sort: Sort, index: number) {
    if (!sort.active || sort.direction == "") {
      return;
    }
    if (sort.active === "usingLevel") {
      // Display highest-level users in ascending and lowest-level in descending order
      this.staffLocationList[index].staff = this.staffLocationList[index].staff.sort((a, b) => {
        const orderA = this.userSortOrder[a.usingLevel] ?? 999;
        const orderB = this.userSortOrder[b.usingLevel] ?? 999;
  
        return sort.direction === "asc"
          ? orderA - orderB
          : orderB - orderA;
      });
  
      return;
    }
    this.staffLocationList[index].staff = orderBy(
      this.staffLocationList[index].staff,
      sort.active,
      sort.direction
    );
  }

  ngOnInit() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.watchRoleSymbols();
    this.loadRoleSymbols();

    // A failed location list leaves only the customer level, so the page still opens
    this.api.getLocations().pipe(catchError(() => of([]))).subscribe((res) => {
      this.locationList = res ?? [];
      if (this.user.locationId.startsWith("C0")) {
        //Add customer information into location list since staff members can be in root level
        const customerInfo = JSON.parse(localStorage.getItem("customerInfo"));
        const customer: LocationModel = new LocationModel();
        customer.id = customerInfo.customerId;
        customer.name = customerInfo.name;
        customer.address = customerInfo.address;
        customer.postCode = customerInfo.postCode;
        customer.postOffice = customerInfo.postOffice;
        this.locationList = !this.locationList ? this.locationList = [] : this.locationList;
        this.locationList.unshift(customer);
      }
      // The staff list may have arrived first
      if (this.staffList) {
        this.parseStaffList();
      }
    });
    this.filteredStaffList$ = combineLatest([
      this.fullStaffListObs$,
      this.queryForFilter$.pipe(startWith("all")),
    ])
      .pipe(
        map(([fullStaffList, queryString]) => {
          // filter staff list on given fields of each profile
          if (queryString !== "all") {
            this.showLocationsWithoutStaff = true;
            this.filterON = true;

            return fullStaffList.filter((profile) => {
              const whereToSearchFields = [
                "username",
                "firstName",
                "familyName",
                "id",
                "email",
                "gsmNumber",
              ];
              let match = false;

              whereToSearchFields.forEach((field) => {
                if (match) {
                  return;
                }
                match = profile[field]?.toLowerCase().includes(queryString) ?? false;
              });

              return match;
            });

            // return the full list (unfiltered)
          } else {
            this.filterON = false;
            return fullStaffList;
          }
        })
      )
      .subscribe((res) => {
        if (res) {
          this.staffList = res;
        } else {
          this.staffList = [];
        }

        //Check if user comes with URL containing the staff ID
        let id;
        //Get ID from the URL
        this.route.params.subscribe((params) => {
          id = params["id"];
        });
        //If ID was found from the URL find correct staff from the staff list and set that as a selected staff
        if (id) {
          this.api.getStaffInfo(id).subscribe((res) => {
            this.selectedStaff = res;
          });
        }

        //Parse staff list so each staff is under correct location
        this.parseStaffList();
      });

    fromEvent(this.staffSearchButton.nativeElement, "click")
      .pipe(
        map(() =>
          this.staffSearchInput.nativeElement.value.toLowerCase().trim()
        )
      )
      .subscribe((query: string) => this.applyFilterCriteria(query));
  }

  // two use cases: one for Enter button click and one for Search button click
  applyFilterCriteria(query: string) {
    if (query.length > 0) {
      this.filterCriteria = query;
      this.queryForFilter.next(query);
      this.showLocationsWithoutStaff = false;
    } else {
      this.clearFilter();
    }
  }

  onEnterKeydown(event) {
    const query = event.target.value.toLowerCase().trim();
    this.applyFilterCriteria(query);
  }

  clearFilter() {
    this.staffSearchInput
      ? ((<HTMLInputElement>this.staffSearchInput.nativeElement).value = "")
      : null;
    this.filterCriteria = "";
    this.showLocationsWithoutStaff = false;
    this.queryForFilter.next("all");
  }
}
